import { baseApi } from "../baseApi";
import { pick } from "../util";
import type {
  Category,
  CreateCategoryInput,
  UpdateCategoryInput,
} from "../types";

const CATEGORY_FIELDS = `
  id
  organizationId
  name
  code
  description
  parentId
  isActive
  status
  createdAt
  updatedAt
`;

export const categoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCategories: builder.query<Category[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Categories($organizationId: String) {
              categories(organizationId: $organizationId) {
                ${CATEGORY_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<Category[]>(res, "categories"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((c) => ({ type: "Category" as const, id: c.id })),
              { type: "Category" as const, id: "LIST" },
            ]
          : [{ type: "Category" as const, id: "LIST" }],
    }),

    getCategory: builder.query<Category, string>({
      query: (id) => ({
        body: {
          query: `
            query Category($id: String!) {
              category(id: $id) {
                ${CATEGORY_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Category>(res, "category"),

      providesTags: (_result, _err, id) => [{ type: "Category", id }],
    }),

    createCategory: builder.mutation<Category, CreateCategoryInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateCategory($input: CreateCategoryInput!) {
              createCategory(input: $input) {
                ${CATEGORY_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<Category>(res, "createCategory"),

      invalidatesTags: [{ type: "Category", id: "LIST" }],
    }),

    updateCategory: builder.mutation<
      Category,
      { id: string; input: UpdateCategoryInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateCategory($id: String!, $input: UpdateCategoryInput!) {
              updateCategory(id: $id, input: $input) {
                ${CATEGORY_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<Category>(res, "updateCategory"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Category", id },
        { type: "Category", id: "LIST" },
      ],
    }),

    deleteCategory: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteCategory($id: String!) {
              deleteCategory(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteCategory"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Category", id },
        { type: "Category", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useLazyGetCategoriesQuery,
  useGetCategoryQuery,
  useLazyGetCategoryQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} = categoryApi;
